import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["template", "container", "row", "idField"]
  static values = { 
    minRows: { type: Number, default: 1 },
    paramName: String,
    alwaysShowDelete: { type: Boolean, default: false }
  }

  connect() {
    this.ensureMinRows();
    this.rowTargets.forEach(row => this.prepareRow(row));
    this.updateDeleteButtons();
  }

  add(event) {
    if (event) event.preventDefault();
    if (!this.templateTarget.content) return;

    const fragment = this.templateTarget.content.cloneNode(true);
    const row = fragment.querySelector("[data-add-to-form-target='row']");
    if (!row) return;

    this.clearValues(row);
    this.prepareRow(row);

    this.containerTarget.appendChild(fragment);

    this.ensureMinRows();
    this.updateDeleteButtons();
  }

  remove(event) {
    event.preventDefault();
    if (!this.alwaysShowDeleteValue && this.rowTargets.length <= this.minRowsValue) return;

    const row = event.currentTarget.closest("[data-add-to-form-target='row']");
    if (!row) return;
    
    row.remove();
    this.ensureMinRows();
    this.updateDeleteButtons();
    this.element.dispatchEvent(new CustomEvent("row-removed", { bubbles: true }));
  }

  ensureMinRows() {
    while (this.rowTargets.length < this.minRowsValue) {
      const fragment = this.templateTarget.content.cloneNode(true);
      const row = fragment.querySelector("[data-add-to-form-target='row']");

      if (row) {
        this.clearValues(row);
        this.prepareRow(row);
      }

      this.containerTarget.appendChild(fragment);
    }
  }

  updateDeleteButtons() {
    const canRemove = this.alwaysShowDeleteValue || this.rowTargets.length > this.minRowsValue;
    this.rowTargets.forEach(row => {
      const deleteBtn = row.querySelector("[data-action*='add-to-form#remove']");
      if (!deleteBtn) return

      if (canRemove) {
        deleteBtn.classList.remove("hidden");
        deleteBtn.disabled = false;
      } else {
        deleteBtn.classList.add("hidden");
        deleteBtn.disabled = true;
      }
    })
  }

  ensureRowHasId(row) {
    const idField = row.querySelector("[data-add-to-form-target='idField']");
    if (!idField) return;

    if (!idField.value || idField.value.trim() === "") {
      idField.value = `item_${this.randomHex(12)}`;
    }
  }

  prepareRow(row) {
    this.ensureRowHasId(row);
    this.syncDynamicIdentifiers(row);
    this.restoreRowValue(row);
  }

  syncValue(event) {
    const row = event.currentTarget.closest("[data-add-to-form-target='row']");
    const field = row && row.querySelector("[data-value-field]");
    if (field) field.value = event.currentTarget.value;
  }

  restoreRowValue(row) {
    const field = row.querySelector("[data-value-field]");
    if (!field || !field.value) return;

    const radio = row.querySelector(`input[type="radio"][value="${field.value}"]`);
    if (radio) radio.checked = true;
  }

  syncDynamicIdentifiers(row) {
    const idField = row.querySelector("[data-add-to-form-target='idField']");
    if (!idField || !idField.value) return;

    const rowToken = this.domSafeToken(idField.value);

    row.querySelectorAll("[data-row-dom-id-base]").forEach((element) => {
      const newId = `${element.dataset.rowDomIdBase}_${rowToken}`;
      element.id = newId;

      const src = element.getAttribute('src');
      if (src && src.includes('frame_id=')) {
        element.setAttribute('src', src.replace(/frame_id=[^&]*/, `frame_id=${newId}`));
      }
    });

    row.querySelectorAll("[data-row-dom-for-base]").forEach((element) => {
      element.setAttribute("for", `${element.dataset.rowDomForBase}_${rowToken}`);
    });

    row.querySelectorAll("[data-row-dom-name-base]").forEach((element) => {
      element.name = `${element.dataset.rowDomNameBase}_${rowToken}`;
    });

    row.querySelectorAll("[data-row-dom-input-query-base]").forEach((element) => {
      element.dataset.drivingSchoolWidgetsInstructorSelectorInputQuerySelector =
        `#${element.dataset.rowDomInputQueryBase}_${rowToken}`;
    });
  }

  domSafeToken(value) {
    return value.toString().trim().replace(/[^a-zA-Z0-9_-]/g, "_");
  }

  randomHex(length) {
    const bytes = new Uint8Array(Math.ceil(length / 2));
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("").slice(0, length);
  }

  clearValues(element) {
    element.querySelectorAll("input, textarea, select").forEach(el => {
      if (el.dataset.addToFormPreserveValue === "true") return;
      if (el.type === "hidden") return;

      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = false;
      } else {
        el.value = "";
      }
    })
  }
}
